/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Feature flag configuration.
 * 
 * Feature flags are resolved at BUILD TIME via Vite's import.meta.env injection.
 * They control which components and API endpoints are included in the production
 * bundle. Disabled flags result in dead-code elimination (tree-shaking) of their
 * associated routes, components, and service calls.
 * 
 * Flag values are controlled per environment via ADO variable groups
 * (housien-ff-testing, housien-ff-staging, housien-ff-production) which are
 * passed to the Vite build step as environment variables.
 * 
 * Runtime overrides via the dev cockpit are stored in sessionStorage
 * (only available when FF_DEV_COCKPIT=true at build time).
 */

import { defineFlag } from './feature-flag-registry';
export { parseBooleanFlag } from './feature-flag-registry-compat';

export const FF_BLOG = defineFlag(
  'FF_BLOG',
  import.meta.env.FF_BLOG,
  false,
  'Controls whether the blog feature is enabled.',
);

export const FF_APP = defineFlag(
  'FF_APP',
  import.meta.env.FF_APP,
  false,
  'Controls whether the authenticated app routes are enabled.',
);

export const FF_EARLY_ACCESS = defineFlag(
  'FF_EARLY_ACCESS',
  import.meta.env.FF_EARLY_ACCESS,
  false,
  'Controls whether the application is in early access (pre-launch) mode.',
);

export const FF_DEV_DESIGN_SYSTEM = defineFlag(
  'FF_DEV_DESIGN_SYSTEM',
  import.meta.env.FF_DEV_DESIGN_SYSTEM,
  import.meta.env.FF_DEV_COCKPIT === 'true',
  'Controls whether the design system documentation area is enabled.',
);

export const FF_MOCK_AUTH = defineFlag(
  'FF_MOCK_AUTH',
  import.meta.env.FF_MOCK_AUTH,
  import.meta.env.FF_DEV_COCKPIT === 'true',
  'Enables mock authentication in development. Set FF_MOCK_AUTH=false to use real OIDC locally.',
  { isMockToggle: true, localOnly: true },
);

export const FF_MOCK_ANTIBOT = defineFlag(
  'FF_MOCK_ANTIBOT',
  import.meta.env.FF_MOCK_ANTIBOT,
  false,
  'Bypasses anti-bot token generation and backend validation. Only works on local environment.',
  { requiresDebug: true, isMockToggle: true, localOnly: true },
);

export const FF_MOCK_PAYMENTS = defineFlag(
  'FF_MOCK_PAYMENTS',
  import.meta.env.FF_MOCK_PAYMENTS,
  import.meta.env.FF_DEV_COCKPIT === 'true',
  'Bypasses Payien payment integration with mock checkout flow.',
  { isMockToggle: true, localOnly: true },
);

export const FF_APP_WHATSAPP = defineFlag(
  'FF_APP_WHATSAPP',
  import.meta.env.FF_APP_WHATSAPP,
  false,
  'Controls whether the WhatsApp identity feature is accessible (route, nav item, mobile FAB).',
);

export const FF_APP_ADDONS = defineFlag(
  'FF_APP_ADDONS',
  import.meta.env.FF_APP_ADDONS,
  false,
  'Controls whether add-ons are offered: the subscription add-ons card and the ' +
    'quick purchase list in the usage popover. Mirrors the backend flag of the ' +
    'same name, which gates the two add-on endpoints. Purchases bill through ' +
    'Payien for real, so this stays off until the feature is ready.',
);

export const FF_APP_CALENDAR = defineFlag(
  'FF_APP_CALENDAR',
  import.meta.env.FF_APP_CALENDAR,
  false,
  'Controls whether the calendar feature is accessible.',
);

export const FF_APP_LAUNCHES = defineFlag(
  'FF_APP_LAUNCHES',
  import.meta.env.FF_APP_LAUNCHES,
  false,
  'Controls whether Controle de Lançamento (launch allocation board) is accessible.',
);

export const FF_APP_NOTIFICATIONS = defineFlag(
  'FF_APP_NOTIFICATIONS',
  import.meta.env.FF_APP_NOTIFICATIONS,
  false,
  'Controls whether the notifications feature is accessible.',
);

export const FF_APP_PRODUCT_TOUR = defineFlag(
  'FF_APP_PRODUCT_TOUR',
  import.meta.env.FF_APP_PRODUCT_TOUR,
  false,
  'Controls whether the guided product tour (menu tour and per-screen help) is shown.',
);

export const FF_APP_FACEBOOK = defineFlag(
  'FF_APP_FACEBOOK',
  import.meta.env.FF_APP_FACEBOOK,
  false,
  'Controls whether the Facebook Page integration module is accessible (publishing, leads, settings).',
);

export const FF_APP_FACEBOOK_MARKETPLACE = defineFlag(
  'FF_APP_FACEBOOK_MARKETPLACE',
  import.meta.env.FF_APP_FACEBOOK_MARKETPLACE,
  false,
  'Controls whether the Facebook Marketplace channel is visible in publish dialog and Facebook settings. Requires FF_APP_FACEBOOK=true.',
);

export const FF_APP_FACEBOOK_ADS = defineFlag(
  'FF_APP_FACEBOOK_ADS',
  import.meta.env.FF_APP_FACEBOOK_ADS,
  false,
  'Controls whether Meta Ads-billed features (lead ads, boosting a published post) are offered anywhere in the app -- publish dialog, Facebook settings config, and payment-method warnings. Gated separately from FF_APP_FACEBOOK because these specifically require the ads_management Meta App Review permission, which the base Facebook connection does not.',
);

export const FF_APP_INSTAGRAM_NATIVE = defineFlag(
  'FF_APP_INSTAGRAM_NATIVE',
  import.meta.env.FF_APP_INSTAGRAM_NATIVE,
  false,
  'Controls whether the native Instagram Business Login connection (no Facebook Page required) is accessible.',
);

export const FF_APP_PORTALS_INTEGRATION = defineFlag(
  'FF_APP_PORTALS_INTEGRATION',
  import.meta.env.FF_APP_PORTALS_INTEGRATION,
  true,
  'Controls whether the listing syndication module is accessible: pushing ' +
    'properties to third-party real estate portals (OLX, ZAP, Viva Real, ' +
    "Chaves na Mão). Unrelated to FF_APP_AGENCY_SITE, which gates the agency's " +
    'own public site.',
);

export const FF_APP_OLX = defineFlag(
  'FF_APP_OLX',
  import.meta.env.FF_APP_OLX,
  false,
  'Controls whether OLX (Autoupload API) is offered: the OLX block in Portais ' +
    'settings, OLX in the publish dialog and OLX rows in the publications ' +
    'center. Separate from FF_APP_PORTALS_INTEGRATION so OLX can be piloted ' +
    'without releasing the ZAP/Viva Real/Chaves na Mão feeds, and off whenever ' +
    'that module is off.',
  { parent: 'FF_APP_PORTALS_INTEGRATION' },
);

export const FF_APP_CANAL_PRO = defineFlag(
  'FF_APP_CANAL_PRO',
  import.meta.env.FF_APP_CANAL_PRO,
  false,
  'Controls the Canal Pro (Grupo OLX) card in Portais settings: the VRSync ' +
    'feed link that publishes on ZAP, Viva Real and OLX, and the private lead ' +
    'URL the agency pastes into Canal Pro. Separate from FF_APP_PORTAL_FEEDS ' +
    '(the older per-portal feed cards); off whenever FF_APP_PORTALS_INTEGRATION is off.',
  { parent: 'FF_APP_PORTALS_INTEGRATION' },
);

export const FF_APP_CHAVES_NA_MAO = defineFlag(
  'FF_APP_CHAVES_NA_MAO',
  import.meta.env.FF_APP_CHAVES_NA_MAO,
  false,
  'Controls Chaves na Mão: its card in Portais settings (private XML feed URL, ' +
    'lead credentials, read status), the option in the publish dialog and its ' +
    'publication rows. Separate from FF_APP_PORTAL_FEEDS (the older per-portal ' +
    'feed cards); off whenever FF_APP_PORTALS_INTEGRATION is off.',
  { parent: 'FF_APP_PORTALS_INTEGRATION' },
);

export const FF_APP_PORTAL_FEEDS = defineFlag(
  'FF_APP_PORTAL_FEEDS',
  import.meta.env.FF_APP_PORTAL_FEEDS,
  false,
  'Controls whether the XML-feed portals (ZAP Imóveis, Viva Real, Chaves na ' +
    'Mão) are offered: their card in Portais settings, their options in the ' +
    'publish dialog and their publication rows. Off while the feeds are not ' +
    'working end to end; off whenever FF_APP_PORTALS_INTEGRATION is off.',
  { parent: 'FF_APP_PORTALS_INTEGRATION' },
);

export const FF_APP_PARTNERSHIPS = defineFlag(
  'FF_APP_PARTNERSHIPS',
  import.meta.env.FF_APP_PARTNERSHIPS,
  false,
  'Controls whether the partnerships wizard is accessible.',
);

export const FF_APP_FUNNEL = defineFlag(
  'FF_APP_FUNNEL',
  import.meta.env.FF_APP_FUNNEL,
  false,
  'Controls whether the Atendimentos and Negócios screens (client-to-deal funnel navigation) are accessible.',
);

export const FF_APP_LEASE = defineFlag(
  'FF_APP_LEASE',
  import.meta.env.FF_APP_LEASE,
  false,
  'Controls whether the Locações area (lease registry) is accessible. Mirrors ' +
    'the backend flag of the same name. Depends on FF_APP_FUNNEL: a lease is ' +
    'drafted from a won deal, so it is off whenever the funnel is off.',
  { parent: 'FF_APP_FUNNEL' },
);

export const FF_APP_SALE = defineFlag(
  'FF_APP_SALE',
  import.meta.env.FF_APP_SALE,
  false,
  'Controls whether the Vendas area (sale records) is accessible. Depends on ' +
    'FF_APP_FUNNEL: a sale record originates from a won deal, so it is off ' +
    'whenever the funnel is off.',
  { parent: 'FF_APP_FUNNEL' },
);

export const FF_APP_GOOGLE_CALENDAR_INTEGRATION = defineFlag(
  'FF_APP_GOOGLE_CALENDAR_INTEGRATION',
  import.meta.env.FF_APP_GOOGLE_CALENDAR_INTEGRATION,
  false,
  'Controls whether the Google Agenda integration section is visible in the sidebar. Only active in testing.',
);

export const FF_APP_CREDIT_RISK = defineFlag(
  'FF_APP_CREDIT_RISK',
  import.meta.env.FF_APP_CREDIT_RISK,
  false,
  'Controls whether the credit risk score tab is visible in client profile.',
);

export const FF_MOCK_CREDIT = defineFlag(
  'FF_MOCK_CREDIT',
  import.meta.env.FF_MOCK_CREDIT,
  import.meta.env.FF_DEV_COCKPIT === 'true',
  'Mocks credit bureau responses with selectable risk band scenarios.',
  { isMockToggle: true, localOnly: true },
);

export const FF_APP_AGENCY_SITE = defineFlag(
  'FF_APP_AGENCY_SITE',
  import.meta.env.FF_APP_AGENCY_SITE,
  true,
  "Controls whether the AgencySite configuration page (the agency's own " +
    'public site) is accessible. Unrelated to FF_APP_PORTALS_INTEGRATION, ' +
    'which gates listing syndication to OLX/ZAP/Viva Real.',
);

export const FF_APP_POST_IMAGE_EDIT = defineFlag(
  'FF_APP_POST_IMAGE_EDIT',
  import.meta.env.FF_APP_POST_IMAGE_EDIT,
  false,
  'Controls whether users can add or remove images from published posts during editing.',
);

export const FF_APP_SPREADSHEETS = defineFlag(
  'FF_APP_SPREADSHEETS',
  import.meta.env.FF_APP_SPREADSHEETS,
  false,
  'Controls whether the Planilhas area is accessible: the spreadsheet menu on ' +
    'the Clientes and Imoveis listings, the two-screen import flow, and the ' +
    'hub route with connected sheets, reports and history.',
);


export const FF_MOCK_SPREADSHEET_API = defineFlag(
  'FF_MOCK_SPREADSHEET_API',
  import.meta.env.FF_MOCK_SPREADSHEET_API,
  false,
  'Serves the spreadsheet screens from in-memory fixtures instead of the ' +
    'backend. Opt-in, and off by default: the import session, preview, ' +
    'settings, commit and undo endpoints all exist and are wired, so leaving ' +
    'this on would mask the real thing. It defaulted to the dev cockpit while ' +
    'the backend was still being written, which is no longer true. Useful for ' +
    'working on the screens with no stack running.',
  { isMockToggle: true, localOnly: true },
);

export const FF_DEV_QUICK_ENTRY = defineFlag(
  'FF_DEV_QUICK_ENTRY',
  import.meta.env.FF_DEV_QUICK_ENTRY,
  import.meta.env.FF_DEV_COCKPIT === 'true',
  'Gates the dev-cockpit form-filler tool. Active only when cockpit is enabled in non-production.',
  { requiresDebug: true },
);

export const FF_DEV_AGENT_DOCS = defineFlag(
  'FF_DEV_AGENT_DOCS',
  import.meta.env.FF_DEV_AGENT_DOCS,
  import.meta.env.FF_DEV_COCKPIT === 'true',
  'Gates the internal agent capabilities documentation page.',
  { requiresDebug: true },
);

export const FF_APP_PROPERTY_GROUPING = defineFlag(
  'FF_APP_PROPERTY_GROUPING',
  import.meta.env.FF_APP_PROPERTY_GROUPING,
  false,
  'Controls whether the Imóveis listing can be grouped by attribute (city, ' +
    'neighborhood, category, transaction type, status): the "Agrupar" header ' +
    'menu, the grouped table with aggregate headers, the Resumo summary cards, ' +
    'and the grouped mobile sections. Child of FF_APP; off by default so the ' +
    'listing stays pixel-identical to today until the feature is ready.',
);
